import {
  ArrowUpRight,
  BellRing,
  CheckCheck,
  History,
  MailOpen,
  RefreshCw,
} from 'lucide-react';
import { useEffect } from 'react';
import { usePagination } from '../../hooks/usePagination';
import { cn } from '../../lib/cn';
import Pagination from '../Pagination';
import SegmentedTabs from '../ui/SegmentedTabs';
import {
  EmptyState,
  MetricCard,
  PageHero,
  SectionCard,
  SectionTitle,
  StatusPill,
} from '../ui/ScreenPrimitives';

function toneForType(type) {
  if (type === 'warning') {
    return 'warning';
  }
  if (type === 'loading') {
    return 'loading';
  }
  if (type === 'success') {
    return 'success';
  }
  if (type === 'error') {
    return 'error';
  }
  return 'neutral';
}

function formatDateTime(value) {
  if (!value) {
    return 'Unknown';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function toTitleCase(value) {
  return String(value || '')
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function isQueueNotification(notification) {
  return typeof notification?.kind === 'string' && notification.kind.startsWith('queue.');
}

function NotificationCard({
  notification,
  onOpenNavigationTarget,
  onOpenQueue,
  onMarkRead,
  onMarkUnread,
}) {
  const isUnread = !notification?.readAt;
  const queueId = notification?.sourceRef?.queueId || '';

  return (
    <article
      className={cn(
        'rounded-[24px] border p-6 shadow-subtle transition lg:p-7',
        isUnread
          ? 'border-app-accent/30 bg-blue-50/60 shadow-panel'
          : 'border-app-border/80 bg-app-surface/95'
      )}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill tone={isUnread ? 'accent' : 'neutral'}>
              {isUnread ? 'Unread' : 'Read'}
            </StatusPill>
            <StatusPill tone="neutral">{toTitleCase(notification?.kind)}</StatusPill>
            {notification?.familyId ? <StatusPill tone="family">Family</StatusPill> : null}
          </div>
          <p className="mt-3 text-lg font-semibold tracking-tight text-app-ink">
            {notification?.title || 'Notification'}
          </p>
          <p className="mt-2 text-sm leading-6 text-app-muted">
            {notification?.message || 'No message provided.'}
          </p>
        </div>
        <div className="text-right text-xs font-medium uppercase tracking-[0.14em] text-app-muted">
          {formatDateTime(notification?.createdAt)}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {notification?.actorName ? (
          <StatusPill tone="neutral">From {notification.actorName}</StatusPill>
        ) : null}
        {notification?.detailItems?.length ? (
          <StatusPill tone="neutral">
            {notification.detailItems.length} detail
            {notification.detailItems.length === 1 ? '' : 's'}
          </StatusPill>
        ) : null}
      </div>

      {notification?.detailItems?.length ? (
        <div className="mt-5 space-y-3">
          {notification.detailItems.map((detail) => (
            <div
              className="rounded-2xl border border-app-border/70 bg-app-shell px-4 py-3"
              key={detail.id}
            >
              <div className="flex flex-wrap items-center gap-2">
                <StatusPill tone="neutral">{toTitleCase(detail.kind)}</StatusPill>
                <StatusPill tone={detail.status === 'failed' ? 'error' : detail.status === 'executed' ? 'success' : 'neutral'}>
                  {toTitleCase(detail.status)}
                </StatusPill>
              </div>
              <p className="mt-3 text-sm font-semibold text-app-ink">{detail.title}</p>
              <p className="mt-1 text-sm leading-6 text-app-muted">{detail.message}</p>
              {detail.navigationTarget ? (
                <div className="mt-3">
                  <button
                    className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                    type="button"
                    onClick={() => onOpenNavigationTarget(detail.navigationTarget)}
                  >
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    <span>Open detail target</span>
                  </button>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <div className="mt-5 flex flex-wrap gap-2 border-t border-app-border/60 pt-5">
        {notification?.navigationTarget ? (
          <button
            className="inline-flex items-center gap-2 rounded-xl bg-app-accent px-3 py-2 text-sm font-semibold text-white transition hover:bg-app-accent-strong"
            type="button"
            onClick={() => onOpenNavigationTarget(notification.navigationTarget)}
          >
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
            <span>Open destination</span>
          </button>
        ) : null}
        {queueId ? (
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={() => onOpenQueue(queueId)}
          >
            <History className="h-4 w-4" aria-hidden="true" />
            <span>Open queue</span>
          </button>
        ) : null}
        {isUnread ? (
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
            type="button"
            onClick={() => onMarkRead(notification.id)}
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            <span>Mark read</span>
          </button>
        ) : (
          <button
            className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-3 py-2 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
            type="button"
            onClick={() => onMarkUnread(notification.id)}
          >
            <MailOpen className="h-4 w-4" aria-hidden="true" />
            <span>Mark unread</span>
          </button>
        )}
      </div>
    </article>
  );
}

function NotificationsView({
  notifications,
  unreadCount,
  statusFilter,
  notificationsStatus,
  isLoadingNotifications,
  onStatusFilterChange,
  onRefresh,
  onOpenNavigationTarget,
  onOpenQueue,
  onMarkRead,
  onMarkUnread,
}) {
  const notificationsPagination = usePagination(notifications, 10);
  const resetNotificationsPage = notificationsPagination.goToFirstPage;

  useEffect(() => {
    resetNotificationsPage();
  }, [notifications, resetNotificationsPage]);

  const queueNotificationsCount = notifications.filter(isQueueNotification).length;
  const familyNotificationsCount = notifications.filter((item) => item.familyId).length;

  return (
    <section className="w-full max-w-[1380px] text-app-ink">
      <PageHero
        eyebrow="Inbox"
        title="Notifications"
        lede="Review unread updates from queue execution, family activity, and shared workspace changes."
        bearyMessage={notificationsStatus.type === 'loading' ? 'Beary is checking family notifications.' : undefined}
        bearyPreset="loading"
        action={<StatusPill tone={toneForType(notificationsStatus.type)}>{notificationsStatus.message}</StatusPill>}
      />

      <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label="Unread" value={unreadCount} icon={BellRing} />
        <MetricCard label="Visible" value={notifications.length} icon={MailOpen} />
        <MetricCard label="Queue updates" value={queueNotificationsCount} icon={History} />
        <MetricCard label="Family updates" value={familyNotificationsCount} icon={CheckCheck} />
      </div>

      <div className="mt-6">
        <SectionCard>
          <SectionTitle
            title="Inbox"
            copy="Filter by read state, open the destination screen, or jump back to the source queue."
            action={
              <button
                className="inline-flex items-center gap-2 rounded-xl border border-app-border bg-app-surface px-4 py-3 text-sm font-medium text-app-ink transition hover:bg-app-surface-alt"
                type="button"
                onClick={onRefresh}
                disabled={isLoadingNotifications}
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                <span>{isLoadingNotifications ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            }
          />

          <div className="mt-6">
            <SegmentedTabs
              value={statusFilter}
              onValueChange={onStatusFilterChange}
              ariaLabel="Notification filters"
              items={[
                { value: 'all', label: 'All' },
                { value: 'unread', label: 'Unread' },
                { value: 'read', label: 'Read' },
              ]}
              listClassName="w-full flex-wrap justify-start"
            />
          </div>

          {notificationsPagination.totalItems ? (
            <div className="mt-6 space-y-5">
              {notificationsPagination.paginatedItems.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onOpenNavigationTarget={onOpenNavigationTarget}
                  onOpenQueue={onOpenQueue}
                  onMarkRead={onMarkRead}
                  onMarkUnread={onMarkUnread}
                />
              ))}

              <Pagination
                currentPage={notificationsPagination.currentPage}
                totalPages={notificationsPagination.totalPages}
                hasNextPage={notificationsPagination.hasNextPage}
                hasPreviousPage={notificationsPagination.hasPreviousPage}
                onGoToPage={notificationsPagination.goToPage}
                onGoToNext={notificationsPagination.goToNextPage}
                onGoToPrevious={notificationsPagination.goToPreviousPage}
                onGoToFirst={notificationsPagination.goToFirstPage}
                onGoToLast={notificationsPagination.goToLastPage}
                startIndex={notificationsPagination.startIndex}
                endIndex={notificationsPagination.endIndex}
                totalItems={notificationsPagination.totalItems}
              />
            </div>
          ) : (
            <div className="mt-6">
              <EmptyState
                title="No notifications in this view"
                copy="Unread items, read history, and queue execution alerts will appear here."
              />
            </div>
          )}
        </SectionCard>
      </div>
    </section>
  );
}

export default NotificationsView;
